import { NettingEngineSummary } from '../../opinions/store/types';

export enum DocumentType {
    ORIGINAL = 'original',
    AMENDMENT = 'amendment',
    RESTATEMENT = 'restatement',
    SUPPORT = 'support',
    REPLICATION = 'replication',
    REPLICATION_AND_AMENDMENT = 'replication and amendment',
    PRE_EXECUTION_DRAFT = 'pre execution draft'
}

export enum SelectedDocument {
    ORIGINAL = 'original',
    SECONDARY = 'secondary',
    SUPPORT = 'support'
}

export enum AnalysisView {
    DOCUMENT = 'document',
    DATASET = 'dataset',
    SPLIT = 'split'
}

export interface LinkedDocument {
    documentId: number;
    documentDescription: string;
    location: string;
    mimeType: string;
    executedDate: string;
    isLegacy?: boolean;
    documentName: string | null;
    agreementType: string | null;
    entityAName: string | null;
    entityBName: string | null;
}

export interface BaseDocumentToSupport {
    documentId: number;
    originalDocumentId: number | null;
    documentDescription: string;
    documentName: string;
}

export interface PrincipalEntity {
    name: string;
    onWatchlist: number;
}

export interface ArkTableDocumentEntity {
    entityId: number | null;
    entityName: string | null;
    myCompanyEntity: number | null;
    entityOnWatchlist: number | null;
    isAgentEntity?: boolean;
    principalEntities?: PrincipalEntity[];
}

export interface ArkDocument {
    documentId: number;
    location: string;
    documentDescription: string;
    documentNameId: number | null;
    documentType: DocumentType | null;
    mimeType: string;
    entityA: number | null;
    entityB: number | null;
    executedDate: string | null;
    datedAsOf: string | null;
    originalDocumentId: number | null;
    linkedDocumentId: number | null;
    createdBy: number;
    modifiedBy: number;
    modifiedDate: string;
    entityAName: string | null;
    entityAMyCompany: number | null;
    entityAOnWatchlist: number | null;
    entityBName: string | null;
    entityBMyCompany: number | null;
    entityBOnWatchlist: number | null;
    documentName: string | null;
    agreementType: string | null;
    agreementTypeId: number | null;
    allDocuments: LinkedDocument[];
    isAgency: number;
    annexDefinitionId?: number;
    analysisStarted: number;
    analysisComplete: number;
    scheduleStartPage: number | null;
    scheduleEndPage: number | null;
    supportDocuments?: LinkedDocument[];
    bothEntitiesCanBeAgent?: boolean;
    agentEntitySelected?: number | null;
    amendmentLinkedDocumentIds?: number[];
    baseDocuments?: BaseDocumentToSupport[];
    isLegacy?: boolean;
    mlDataVersion?: number;
    mlDataVersionConfirmed?: number;
    latestMlDataVersion?: number;
    mlInProgress?: boolean;
    instanceComplete?: boolean;
    ecsStartPage?: number | null;
    ecsEndPage?: number | null;
    agencyAnnexStartPage?: number | null;
    agencyAnnexEndPage?: number | null;
    analyseLater: number;
    agencyEntityId?: number;
    principalEntities?: PrincipalEntity[];
    nettingSummary?: NettingEngineSummary | null;
    nettingCanBeCalculated?: number;
    nettingIsCalculating?: number;
}

export interface ArkTableDocument {
    documentId: number;
    location: string;
    documentDescription: string;
    documentNameId: number | null;
    documentType: DocumentType | null;
    mimeType: string;
    entityA: ArkTableDocumentEntity;
    entityB: ArkTableDocumentEntity;
    executedDate: string | null;
    datedAsOf: string | null;
    originalDocumentId: number | null;
    linkedDocumentId: number | null;
    createdBy: number;
    modifiedBy: number;
    modifiedDate: string;
    documentName: string | null;
    agreementType: string | null;
    agreementTypeId: number | null;
    allDocuments: LinkedDocument[];
    isAgency: number;
    annexDefinitionId?: number;
    analysisStarted: number;
    analysisComplete: number;
    scheduleStartPage: number | null;
    supportDocuments?: LinkedDocument[];
    bothEntitiesCanBeAgent?: boolean;
    agentEntitySelected?: number | null;
    amendmentLinkedDocumentIds?: number[];
    baseDocuments?: BaseDocumentToSupport[];
    isLegacy?: boolean;
    mlDataVersion?: number;
    mlDataVersionConfirmed?: number;
    latestMlDataVersion?: number;
    mlInProgress?: boolean;
    instanceComplete?: boolean;
    agencyAnnexStartPage?: number | null;
    agencyAnnexEndPage?: number | null;
    analyseLater: number;
    documentDisabled?: number;
    nettingSummary?: NettingEngineSummary | null;
    nettingCanBeCalculated?: number;
    nettingIsCalculating?: number;
}

export enum DocumentNameType {
    ISDA_1987 = 1,
    ISDA_1992 = 2,
    ISDA_2002 = 3,
    NY_LAW_CSA_1994 = 4,
    ENGLISH_LAW_CSA_1995 = 5,
    NY_LAW_VM_CSA = 6,
    ENGLISH_LAW_VM_CSA = 7,
    NY_2016_IM_CSA = 8,
    ENGLISH_2016_IM_CSD = 9,
    NY_2018_IM_CSA = 10,
    ENGLISH_2018_IM_CSD = 11,
    CLEARSTREAM_CTA = 12,
    EUROCLEAR_CTA = 13,
    CLEARSTREAM_SA = 14,
    EUROCLEAR_SA = 15,
    ACCOUNT_CONTROL_AGREEMENT = 16,
    GMRA_1995 = 17,
    GMRA_2000 = 18,
    GMRA_2011 = 19,
    OSLA_1995 = 20,
    GMSLA_2000 = 21,
    GMSLA_2010 = 22,
    MRA = 23,
    MSLA = 24,
    CDEA_2014 = 25,
    CDEA_2016 = 26,
    CDEA_2017 = 27,
    EFET = 28,
    SIDE_LETTER = 29,
    GUARANTEE = 30,
    LETTER_OF_CREDIT = 31,
    ISDA_NETTING_OPINION = 32,
    ICMA_NETTING_OPINION = 33,
    ISLA_NETTING_OPINION = 34,
    CONFIDENTIALITY_AGREEMENT = 35,
    DISTRIBUTION_AGREEMENT = 36,
    FUND_SIDE_LETTER = 37,
    INVESTMENT_MANAGEMENT_AGREEMENT = 38,
    FACILITY_AGREEMENT = 39,
    FMI_RULES = 40,
    FMI_RULES_2 = 41,
    RAF_FMI_MEMBERSHIP_RULES_REVIEW = 42,
    RAF_FMI_DOCUMENT_REVIEW_AND_ASSESSMENT = 43,
    RAF_FMI_REVIEW_AND_ASSESSMENT = 44,
    BNYM_GCACA = 45,
    ICT_CONTRACT = 46,
    PRIME_BROKERAGE_AGREEMENT = 47,
    MSFTA_2012 = 48,
    MSFTA_1996 = 49,
    MSLA_1993 = 50,
    MSLA_2000 = 51,
    MSLA_2017 = 52,
    BOILERPLATE = 53,
    MCA = 54,
    CDEA_2012 = 55,
    CSD_1995 = 56,
    INTERNATIONAL_UNIFORM_BROKERAGE_EXECUTION_SERVICES_AGREEMENT = 57,
    GLOBAL_CUSTODY_TERMS_AND_CONDITIONS = 58,
    GMSLA_2010_REHYPOTHECATION = 59,
    PRIME_FINANCE_LOCKUP_AGREEMENT = 60,
    EXCHANGED_TRADED_FUTURES_OPTIONS_PROFESSIONAL_CLIENT_AGREEMENT = 61,
    BNYM_GCACA_ENG = 62,
    TERM_COMMITMENT_ANNEX = 63,
    PORTFOLIO_EQUITY_SWAP_MASTER_CONFIRMATION_AGREEMENT = 64
}

export const NETTING_ENGINE_DOCUMENT_NAME_IDS = [DocumentNameType.ISDA_1992, DocumentNameType.ISDA_2002];

export interface DocumentNettingAnalysis {
    documentId: number;
    canBeCalculated: number;
    nettingAnalysis: NettingEngineSummary;
}
